import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useDialog } from '@smart-builder/components/src/dialog';
import { DialogType } from '@smart-builder/components/src/dialog/ducks/types';
// using direct imports here to reduce the built size of login-rest as much as possible
import { SBButton } from '@smart-builder/components/src/base/SBButton';
import { _t } from '@smart-builder/util';
import { apiLogin, ResponseCodes } from '@smart-builder/rest';

export type AuthorizedApp = {
  xtiId: number;
  displayName: string;
  dllPath?: string;
  appPath: string;
};

export const LoginForm: React.FC = () => {
  const allowedApps = (SB_CONFIG as any).allowedApps as AuthorizedApp[];
  const queryParams = new URLSearchParams(window.location.search);
  const queryXtiId = +(queryParams.get('targetXti') || 0);
  const queryNext = queryParams.get('next');

  const [xtiId, setXtiID] = useState<number>(queryXtiId);
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const showAppSelect = !queryXtiId && allowedApps.length > 1;

  const { dialogOk } = useDialog();

  useEffect(() => {
    if (allowedApps.length > 0 && !allowedApps.find(a => a.xtiId === xtiId)) {
      setXtiID(allowedApps[0].xtiId);
    }
  }, [allowedApps, setXtiID, xtiId]);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // T10865 SM 20221011
    // We used to just disable the button when username or password was blank
    // However, we can't do that anymore as Chrome autofill shows the fields
    //    as filled in, but doesn't update the input's value until the user
    //    interacts with the page (https://stackoverflow.com/questions/55244590/autofill-does-not-trigger-onchange)
    // By the time our button receives the click, the autofill values will have
    //    already reached the input (it happens on mousedown, our click happens on the subsequent mouse up)
    // So, we always enable the button and display a message if the user hasn't actually entered the values.

    if (!userName) {
      await dialogOk({
        message: _t('Please enter a Username'),
        dialogType: DialogType.Error,
      });
      return;
    }

    if (!password) {
      await dialogOk({
        message: _t('Please enter a Password'),
        dialogType: DialogType.Error,
      });
      return;
    }

    setSubmitting(true);

    try {
      const authApp = allowedApps.find(a => a.xtiId === xtiId)!;
      if (authApp.dllPath) {
        // this app has its own DLL, start talking to that DLL from now on - we want to log into the app's DLL, not ours
        SB_CONFIG.serverURL = authApp.dllPath + 'rest';
      }

      const relativeUrl = authApp.appPath;
      let base = window.location.origin + window.location.pathname;
      if (!base.endsWith('/')) {
        base += '/';
      }

      const url = new URL(relativeUrl, base).href;
      await apiLogin(xtiId, userName, password, url);

      // redirect to app
      window.location.href =
        (queryNext && decodeURIComponent(queryNext)) || url;
    } catch (e) {
      console.error(e);
      if (e.code === ResponseCodes.sbUnauthorized) {
        setSubmitting(false);
        await dialogOk({
          title: _t('Login Failed'),
          message:
            e.errorMessage ||
            _t('Username or Password was invalid. Please try again.'),
          dialogType: DialogType.Error,
        });
      } else {
        setSubmitting(false);
        await dialogOk({
          title: _t('Unknown Error'),
          message: _t('Something went wrong. Please try again later.'),
          dialogType: DialogType.Error,
        });
      }
    }
  };

  return (
    <Form onSubmit={onSubmit}>
      {showAppSelect && (
        <Form.Group controlId="appSelect" className="select-container">
          <Form.Label>{_t('Application')}</Form.Label>
          <Form.Select value={xtiId} onChange={e => setXtiID(+e.target.value)}>
            {allowedApps.map(({ xtiId, displayName }) => (
              <option key={xtiId} value={xtiId}>
                {displayName}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      )}
      <Form.Group
        controlId="formUserName"
        className="input-container"
        data-sb-field="username"
      >
        <Form.Label>{_t('Username')}</Form.Label>
        <Form.Control
          type="text"
          autoComplete="username"
          value={userName}
          onChange={e => setUserName(e.target.value)}
        />
      </Form.Group>
      <Form.Group
        controlId="formPassword"
        className="input-container"
        data-sb-field="password"
      >
        <Form.Label>{_t('Password')}</Form.Label>
        <Form.Control
          type="password"
          autoComplete="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
        />
      </Form.Group>
      <SBButton
        variant="primary"
        type="submit"
        disabled={submitting}
        id="sb-login"
      >
        {_t('Login')}
      </SBButton>
    </Form>
  );
};
